import { proxy, snapshot, useSnapshot } from "valtio";
import { post, get } from "@/lib/http";
import { toast } from "sonner";
import { PaymentMethods } from "@/types/stripe/payment-methods.type";
import { PlanActionsEnum } from "@/types/plan.type";

type StripeProxy = {
  isLoading: boolean;
  previousPage: string;

  confirmCheckout: () => void;
  getAccountPaymentMethods: (externalId: string) => Promise<PaymentMethods>;
  generateAccountCheckoutUrl: (externalId: string) => Promise<string>;
  setupAccountPaymentMethods: (
    externalId: string,
    previousPage?: string,
    action?: PlanActionsEnum
  ) => any;
};

const stripeProxy = proxy<StripeProxy>({
  isLoading: false,
  previousPage: window.sessionStorage.getItem("stripePreviousPage") ?? "/",

  confirmCheckout: () => {
    toast.success("Payment Method successfully configured !");
    window.sessionStorage.removeItem("stripePreviousPage");
    stripeProxy.isLoading = false;
  },

  getAccountPaymentMethods: async (
    externalId: string
  ): Promise<PaymentMethods> => {
    try {
      const paymentMethods = await get<PaymentMethods>(
        `/api/account/${externalId}/payment_methods`
      );
      return paymentMethods;
    } catch (error) {
      console.error(`Error when fetching account's payment method: ${error}`);
      toast.error(`Error when fetching account's payment methods`);
      throw error;
    }
  },

  generateAccountCheckoutUrl: async (externalId: string): Promise<string> => {
    stripeProxy.isLoading = true;
    try {
      const checkoutUrl = await post<string, unknown>(
        `/api/account/${externalId}/checkout_url`,
        {}
      );
      stripeProxy.isLoading = false;
      return checkoutUrl;
    } catch (error) {
      toast.error("Error when generating Stripe's checkout url");
      throw error;
    } finally {
      stripeProxy.isLoading = false;
    }
  },

  setupAccountPaymentMethods: async (
    externalId: string,
    previousPage?: string,
    action?: PlanActionsEnum
  ) => {
    stripeProxy.isLoading = true;
    const checkoutUrl =
      await stripeProxy.generateAccountCheckoutUrl(externalId);

    if (previousPage) {
      stripeProxy.previousPage = previousPage;
      window.sessionStorage.setItem("stripePreviousPage", previousPage);
    }

    if (action) {
      window.sessionStorage.setItem("stripeCallbackAction", action);
    }

    return (window.location.href = checkoutUrl);
  }
});

export const getStripeSnapshot = () => snapshot(stripeProxy);
export const getStripeProxy = () => stripeProxy;

export const useStripeSnapshot = (
  options?: Parameters<typeof useSnapshot>[1]
) => useSnapshot(stripeProxy, options);

export const useStripeProxy = () => stripeProxy;

export const useStripeStore = (options?: Parameters<typeof useSnapshot>[1]) => {
  return {
    stripeSnapshot: useSnapshot(stripeProxy, options),
    stripeProxy: stripeProxy
  };
};
